import React from 'react';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getPipelineStepRawDatasOfKind} from 'pipelines/StepDataUtils';

import PresentationalStepDataPreview, {
  PersistedStepDatas,
  StepDataRole,
} from './PresentationalStepDataPreview';

export interface PersistedStepDataPreviewProps {
  step: PipelineStep;
  /** Which step datas to render. Defaults to the step's outputs. */
  stepDataRole?: StepDataRole;
  /** Prefix for table client state ids; defaults to a value derived from the step id. */
  clientStateId?: string;
}

/**
 * Wrapper for `<PresentationalStepDataPreview>`, to show a step's persisted inputs or outputs.
 *
 * @package
 */
export default function PersistedStepDataPreview({
  step,
  stepDataRole = StepDataRole.OUTPUT,
  clientStateId,
}: PersistedStepDataPreviewProps): JSX.Element {
  const stepDatas = stepDataRole === StepDataRole.INPUT ? step.inputStepDatas : step.outputStepDatas;

  const persisted: PersistedStepDatas = {};
  stepDatas.forEach((stepData) => {
    persisted[stepData.name] = {
      charts: getPipelineStepRawDatasOfKind(PipelineStepDataKind.CHART, stepData),
      dataFrames: getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, stepData),
      files: getPipelineStepRawDatasOfKind(PipelineStepDataKind.FILE, stepData),
      plates: getPipelineStepRawDatasOfKind(PipelineStepDataKind.PLATE, stepData),
    };
  });

  return (
    <PresentationalStepDataPreview
      stepType={step.stepType}
      stepVersion={step.stepVersion}
      stepDataRole={stepDataRole}
      clientStateId={clientStateId ?? `preview-${step.id}`}
      persisted={persisted}
    />
  );
}
